import React from 'react';
import { HiOutlineExternalLink } from 'react-icons/hi';
import { PiSparkleFill } from 'react-icons/pi';
import { formatCareerPathType, revertPositionTitle } from 'shared-utils';
import { PositionTitle, UIExtendedApplication, UserCareerPathType } from 'types';
import { CompanyLogo } from 'src/components/placementDrives/shared';

type ApplicationWithBadge = UIExtendedApplication & {
	badgeTextForJobseeker?: string | null;
};

interface Props {
	application: ApplicationWithBadge;
}

// Local fallback in case the backend doesn't send badgeTextForJobseeker
const computeJobseekerBadge = (types?: ('auto' | 'manual' | 'you_might_be_interested')[] | null) => {
	if (!types || types.length === 0) return null;
	const hasAuto = types.includes('auto');
	const hasManual = types.includes('manual');
	if (types.includes('you_might_be_interested')) return 'You might be interested';
	if (hasAuto && !hasManual) return null;
	return 'Recommended';
};

const ApplicationCardHeader = ({ application }: Props) => {
	const positionTitle = application.isDriveApplication
		? formatCareerPathType(application.position?.title as UserCareerPathType)
		: formatCareerPathType(revertPositionTitle(application.position?.title as PositionTitle) as UserCareerPathType);
	const companyName = application.position?.company?.name;
	const companyUrl = application.position?.company?.url || '';
	const companyLogo = application.position?.company?.logo || '';
	const badgeText = application?.badgeTextForJobseeker ?? computeJobseekerBadge(application?.recommendationTypes);

	return (
		<div>
			{badgeText ? (
				<span className="mb-3 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#EEEDFD] to-[#F7EEFF] px-2.5 py-1 text-[11px] font-[700] text-[#5B3FD6] md:hidden">
					<PiSparkleFill className="h-3 w-3" />
					{badgeText}
				</span>
			) : null}
		<div className="flex items-center gap-3.5">
			<CompanyLogo name={companyName} logo={companyLogo} url={companyUrl} size={52} />
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
					<p className="text-[17px] leading-[24px] font-[800] tracking-[-0.015em] text-[#111E2C] dark:text-white">{positionTitle}</p>
					{badgeText ? (
						<span className="hidden items-center gap-1 rounded-full bg-gradient-to-r from-[#EEEDFD] to-[#F7EEFF] px-2.5 py-0.5 text-[11px] font-[700] text-[#5B3FD6] md:inline-flex">
							<PiSparkleFill className="h-3 w-3" />
							{badgeText}
						</span>
					) : null}
				</div>
				<p className="mt-0.5 flex items-center gap-1.5 text-[14px] leading-[20px] text-[#555770] dark:text-[#C7C9D9]">
					<span className="truncate">{companyName}</span>
					{companyUrl ? (
						<button
							type="button"
							aria-label="Open company website"
							onClick={e => {
								e.stopPropagation();
								window.open(companyUrl);
							}}
							className="shrink-0 text-[#4F46E5] hover:text-[#3730A3]"
						>
							<HiOutlineExternalLink className="h-4 w-4" />
						</button>
					) : null}
				</p>
			</div>
		</div>
		</div>
	);
};

export default ApplicationCardHeader;
